import { useEffect, useMemo, useRef, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import ClearIcon from '@mui/icons-material/Clear';
import { debounceAsync } from 'utils/misc/debounce';
import {
	searchHRDepartments,
	searchHRJobTitles,
	searchJobCategories,
	searchJobCategoryMappingDepartments,
	searchJobCategoryMappingJobTitles,
	searchTrainingTracks,
} from '../_api';
import { AUTOCOMPLETE_SEARCH_TYPE } from '../_const';

/**
 * API-Searchable textbox component
 * @param {string} type
 * @param {boolean} archival
 * @param {string} label
 * @param {string} placeholder
 * @param {function} handleChange
 * @param {function} handleKeystroke
 * @param {any} value
 * @param {boolean} error
 * @param {function} search
 * @param {function} onBlur
 * @param {object} props
 * @returns searchable textbox
 */
const AsyncAutocomplete = ({
	type,
	archival = false,
	label,
	placeholder,
	handleChange = () => {},
	handleKeystroke = () => {},
	value,
	error,
	onBlur = () => {},
	...props
}) => {
	const refTextField = useRef(null);
	const [sInputValue, setInputValue] = useState();
	const [sOptions, setOptions] = useState([]);
	const [sLoading, setLoading] = useState(false);

	const fetch = useMemo(() => {
		return debounceAsync(async ({ value, searchArchive }) => {
			setLoading(true);

			let searchResults = [];
			switch (type) {
				case AUTOCOMPLETE_SEARCH_TYPE.DEPARTMENT:
					searchResults = await searchHRDepartments(value, true);
					break;
				case AUTOCOMPLETE_SEARCH_TYPE.JOB_TITLE:
					searchResults = await searchHRJobTitles(value, true);
					break;
				case AUTOCOMPLETE_SEARCH_TYPE.JOB_CATEGORY:
					searchResults = await searchJobCategories(
						value,
						true,
						searchArchive
					);
					break;
				case AUTOCOMPLETE_SEARCH_TYPE.MAPPING_DEPARTMENT:
					searchResults = await searchJobCategoryMappingDepartments(
						value,
						true
					);
					break;
				case AUTOCOMPLETE_SEARCH_TYPE.MAPPING_JOB_TITLE:
					searchResults = await searchJobCategoryMappingJobTitles(
						value,
						true
					);
					break;
				case AUTOCOMPLETE_SEARCH_TYPE.TRAINING_TRACK:
					searchResults = await searchTrainingTracks(value, true);
					break;
				default:
					searchResults = [];
					break;
			}
			setOptions(searchResults);

			setLoading(false);
		});
	}, [type]);

	useEffect(() => {
		let active = true;

		if (!!!sInputValue || sInputValue === '') {
			setOptions([]);
			return undefined;
		}

		fetch({ value: sInputValue, searchArchive: archival }, results => {
			if (active) {
				setOptions(...results);
			}
		});

		return () => {
			active = false;
		};
	}, [sInputValue, fetch, archival]);

	return (
		<Autocomplete
			value={value}
			onChange={(_event, newValue) => {
				setInputValue(newValue);
				handleChange(newValue);
			}}
			onInputChange={(_event, newInputValue) => {
				setInputValue(newInputValue);
				handleKeystroke(newInputValue);
			}}
			options={sOptions ?? []}
			filterOptions={options => options}
			renderInput={params => (
				<TextField
					{...params}
					inputRef={refTextField}
					label={label}
					placeholder={placeholder}
					fullWidth
					error={error}
					onBlur={onBlur}
				/>
			)}
			autoComplete
			clearIcon={
				sLoading ? (
					<CircularProgress size={24} variant='indeterminate' />
				) : (
					<ClearIcon fontSize={'small'} />
				)
			}
			noOptionsText={
				sInputValue?.length > 0
					? 'No options matching your search'
					: 'Please type to search...'
			}
			disablePortal={true}
			{...props}
		/>
	);
};

export default AsyncAutocomplete;
